/**
 * English copy. This file is the source of truth for the shape of every
 * locale: `Copy` is derived from it, so adding a key here makes every other
 * locale fail to compile until it is translated.
 *
 * Two halves, and the split matters:
 *   `ui`: read with the eyes. Labels, controls, screen-reader text.
 *   `speech`: heard with the ears. Everything the assistant says out loud.
 *
 * Speech lines are written to be spoken, not read: short clauses, no
 * parentheses, no punctuation a synthesiser will mangle. Where a line has
 * several phrasings, the assistant rotates through them so a returning user
 * does not hear the same sentence every single day.
 */

export const en = {
  ui: {
    appName: 'Namu',
    tagline: 'Talk to it in your own language.',

    // Welcome / wake
    beginLabel: 'Tap to begin',
    resumeLabel: 'Tap to talk',

    // Session status. Shown small under the orb and announced to screen readers.
    status: {
      idle: 'Ready',
      permission: 'Waiting for microphone',
      connecting: 'Connecting',
      listening: 'Listening',
      userSpeaking: 'Hearing you',
      processing: 'Thinking',
      modelSpeaking: 'Speaking',
      interrupted: 'Stopped',
      reconnecting: 'Reconnecting',
      ended: 'Ended',
      error: 'Something went wrong',
      muted: 'Microphone off',
      held: 'On hold',
    },

    /**
     * A wait that has gone past what anybody would read as ordinary.
     *
     * Not a status line. The orb carries every normal state and these words
     * appear only when a turn has taken long enough that stillness stops
     * meaning "thinking" and starts meaning "broken". A cold container is over
     * a minute, and a voice interface with nothing on screen gives a person no
     * way to tell one from the other.
     */
    waiting: {
      connecting: 'Still connecting.',
      thinking: 'Namu is still thinking.',
      waking: 'Namu is waking up. The first answer after a quiet spell can take a minute.',
    },

    // Controls. Names, not captions: the rail shows glyphs only.
    control: {
      mute: 'Turn microphone off',
      unmute: 'Turn microphone on',
      hold: 'Hold the conversation',
      resume: 'Resume the conversation',
      end: 'End conversation',
      start: 'Start conversation',
      captionsOn: 'Show captions',
      captionsOff: 'Hide captions',
      repeat: 'Say that again',
      skip: 'Skip this question',
      back: 'Back',
      close: 'Close',
      retry: 'Try again',
      settings: 'Settings',
      profile: 'What Namu knows',
      // Deliberately not "Allow". Apple's guidelines forbid a priming screen
      // that mirrors the system alert, and nobody grants anything on a screen
      // that is not the system alert.
      turnOnMic: 'Turn on the microphone',
      notNow: 'Not now',
    },

    // The onboarding ledger: chips that fill in as the conversation captures
    // each answer, so the user can see and correct what was recorded.
    ledger: {
      title: 'What Namu heard',
      empty: 'Nothing yet. Namu fills this in as you talk.',
      editHint: 'Tap any answer to change it',
      unset: 'Not answered',
      skipped: 'Skipped',
      confirm: 'That is right',
      correct: 'Change this',
    },

    /**
     * The first conversation, on screen. See `src/screens/Welcome`.
     *
     * Four keys, and every one of them exists because something has to be
     * readable rather than only audible. The disclosure is a written record,
     * the typed answer is the way out of a loud market, and the progress label
     * is how somebody who cannot see the petals asks how far along they are.
     */
    onboarding: {
      // Names the record, not the act. "Consent" is a word from a form.
      disclosureTitle: 'What Namu keeps',
      // A real label above a real field. A placeholder disappears the moment
      // somebody starts typing, which is exactly when they most need to know
      // what was asked, and no screen reader treats it as a name.
      typedLabel: 'Type your answer',
      typedSubmit: 'Send',
      progressLabel: 'How much Namu knows so far',
    },

    field: {
      language: 'Language',
      name: 'Name',
      region: 'Where you live',
      ageBand: 'Age',
      gender: 'Gender',
      interests: 'Interests',
      consent: 'Saving your answers',
    },

    ageBand: {
      under18: 'Under 18',
      '18-24': '18 to 24',
      '25-34': '25 to 34',
      '35-49': '35 to 49',
      '50-64': '50 to 64',
      '65plus': '65 or older',
    },

    gender: {
      woman: 'Woman',
      man: 'Man',
      other: 'Another answer',
      undisclosed: 'Prefer not to say',
    },


    // Errors are directions, not apologies: what happened, and the way out.
    error: {
      micDeniedTitle: 'Namu cannot hear you',
      micDeniedBody:
        'The microphone is blocked for this site. Allow it in your browser settings, then try again.',
      micMissingTitle: 'No microphone found',
      micMissingBody: 'Connect a microphone or headset, then try again.',
      unsupportedTitle: 'This browser cannot run Namu',
      unsupportedBody: 'Namu needs microphone access. Try Chrome, Edge or Safari.',
      offlineTitle: 'No connection',
      offlineBody: 'Namu needs a connection to answer. It will reconnect on its own.',
      connectionFailedTitle: 'Connection failed',
      connectionFailedBody: 'Namu could not reach its voice. Tap to try again.',
      genericTitle: 'Something went wrong',
      genericBody: 'The conversation stopped. Start it again to continue.',
      noVoiceTitle: 'No voice for this language yet',
      noVoiceBody:
        'This device has no {language} voice installed, so Namu will show its words instead of speaking them.',
    },

    profile: {
      title: 'What Namu knows',
      subtitle: 'All of this is stored on this phone only.',
      changeByVoice: 'Change by talking',
      deleteAll: 'Delete everything',
      deleteConfirmTitle: 'Delete everything?',
      deleteConfirmBody:
        'Namu will forget your name, region, age, gender and interests. This cannot be undone.',
      deleteConfirmAction: 'Delete',
      cancel: 'Cancel',
      savedOn: 'Saved {date}',
    },

    settings: {
      title: 'Settings',
      language: 'Language',
      // Not "Voice". A persona is a name, a manner and a voice together.
      persona: 'Who you talk to',
      captions: 'Always show captions',
      haptics: 'Vibrate on turns',
      reduceMotion: 'Reduce motion',
      appearance: 'Appearance',
      appearanceSystem: 'Match device',
      appearanceLight: 'Light',
      appearanceDark: 'Dark',
      diagnostics: 'Diagnostics',
      terms: 'Terms of service',
    },

    diagnostics: {
      title: 'Diagnostics',
      turns: 'Turns',
      medianLatency: 'Median reply time',
      retries: 'Times Namu asked again',
      fallbacks: 'Times typing was used',
      interruptions: 'Times you interrupted',
      silenceWindow: 'Current end-of-turn wait',
      reset: 'Clear diagnostics',
      empty: 'No conversations recorded yet.',
    },

    terms: {
      title: 'Terms of service',
      updated: 'Last updated 27 July 2026',
      lead: 'Plain terms for using Namu. Read them once; they are short.',
      sections: [
        {
          heading: 'What Namu is',
          body: 'Namu is a speech-native assistant. It is early software and it will sometimes get things wrong, mishear you, or answer badly. Check anything that matters before you act on it.',
        },
        {
          heading: 'Who can use it',
          body: 'You need to be old enough to agree to terms where you live. If you are not, ask a parent or guardian to agree for you and to use Namu with you.',
        },
        {
          heading: 'What is stored',
          body: 'Your answers are saved on this phone and nowhere else. Your voice is used to understand you while you talk and the audio is not kept. Namu does not make a voiceprint and cannot recognise you by your voice. You can see, change or delete everything from "What Namu knows".',
        },
        {
          heading: 'What Namu is not',
          body: 'Namu does not give medical, legal or financial advice, and it is not an emergency service. In an emergency, contact the services where you are.',
        },
        {
          heading: 'Languages in preview',
          body: 'A language marked "preview" has not yet been checked by a speaker of it. Namu may phrase things oddly or get a word wrong in that language.',
        },
        {
          heading: 'Using Namu fairly',
          body: 'Do not use Namu to harm people, to break the law, or to pretend Namu said something it did not.',
        },
        {
          heading: 'The Namu name and mark',
          body: 'The Namu name, the wordmark and the Blossom belong to Namu. Do not use them for your own product without written permission.',
        },
        {
          heading: 'Changes',
          body: 'These terms may change as Namu grows. When they do, the date at the top changes and Namu will tell you.',
        },
        {
          heading: 'Getting in touch',
          body: 'For anything about these terms, write to legal@namuai.org.',
        },
      ],
    },

    a11y: {
      orb: 'Namu status indicator',
      orbState: 'Namu is {state}',
      liveTranscript: 'Live transcript',
      levelMeter: 'Microphone level',
      skipToControls: 'Skip to controls',
    },

    common: {
      you: 'You',
      namu: 'Namu',
      yes: 'Yes',
      no: 'No',
      of: '{current} of {total}',
      tapToRetry: 'Tap to try again',
      preview: 'Preview',
      previewHint: 'This language has not been checked by a native speaker yet.',
    },
  },

  speech: {
    /** Spoken before the browser's microphone prompt appears. */
    micPrimer: [
      'Now I need to hear you. Your browser will ask for the microphone next.',
    ],
    micDenied: [
      'I cannot hear you yet. The microphone is blocked. You can allow it in your browser settings, then try again.',
    ],
    /**
     * The opening.
     *
     * Spoken, never printed on the welcome screen, and there is no way to say
     * it before the tap: a browser will not let anything make a sound until
     * someone has touched the page. The screen therefore says "Tap to begin"
     * and nothing else, and this is the first thing anybody hears.
     *
     * The lines are grouped by what Namu knows rather than listed as
     * interchangeable phrasings, because two greetings said in rotation is
     * still a recording. `src/greeting` chooses between them from the clock
     * and the last session. Adding a phrasing to any one of these lists is
     * safe; the choice of list is not a copy decision.
     */
    greeting: {
      /**
       * The first thing a stranger hears, in both languages on purpose.
       *
       * The locale at this moment is a guess made from the browser's settings,
       * and the language of the reply is what actually settles it. Opening
       * bilingually is the alternative to a language menu, which is what every
       * other voice product in this market opens with and which is a form.
       *
       * No name is given, and that is the change this line exists to carry.
       * The persona introduces itself, from the backend, in the words and the
       * grammatical gender that belong to it, which is a distinction Hausa
       * makes and English loses. A name written here would be the device
       * making a second claim about who is speaking, and the two come apart
       * the day somebody changes the picker in Settings or an upstream default
       * moves. `src/persona/personas.ts` is the one place that holds a persona
       * name, and `houseRules.test.ts` keeps it that way. What the line has to do
       * is open in both languages, because the reply is what settles which one
       * the conversation happens in.
       *
       * This is also the line the onboarding flow's own opening replaces when
       * that flow is wired in; until then it is what a first visit hears, and
       * it is what somebody who abandoned onboarding hears.
       */
      first: [
        'Sannu. Hello. Talk to me in Hausa or in English, whichever comes easier.',
        'Sannu. Hello. Say what you like, in Hausa or in English.',
      ],

      /** For somebody Namu has a name for. Every phrasing must use it. */
      named: {
        moments: ['Yes, {name}?', '{name}?'],
        sameDay: ['Hello again, {name}.', 'Back again, {name}.'],
        morning: ['Good morning, {name}.'],
        afternoon: ['Good afternoon, {name}.'],
        evening: ['Good evening, {name}.'],
        // Not "good night", which is what somebody says on the way out.
        night: ['Hello, {name}.'],
        week: ['It has been a while, {name}.'],
        month: ['{name}. Good to hear you again.'],
      },

      /**
       * The same eight, for somebody Namu has met before and has no name for.
       *
       * This is not a degraded path. It is what a person who declined to have
       * their answers kept hears every time, and there is nothing about that
       * decision that should cost them a greeting which knows what day it is.
       */
      anonymous: {
        moments: ['Yes?'],
        sameDay: ['Hello again.'],
        morning: ['Good morning.'],
        afternoon: ['Good afternoon.'],
        evening: ['Good evening.'],
        night: ['Hello.'],
        week: ['It has been a while.'],
        month: ['Good to hear you again.'],
      },

      /**
       * Added to the greeting when there is something kept from last time and
       * permission to have kept it. The only line here that requires consent.
       */
      continuity: ['Last time we spoke about {topic}.'],
    },
    listeningPrompt: ['I am listening.'],
    didNotCatch: [
      'Sorry, I did not catch that. Say it again?',
      'I missed that. One more time?',
    ],
    stillThere: ['Are you still there?'],
    goodbye: ['Talk soon.', 'Goodbye.'],
    offline: ['I lost the connection. I will try again.'],
    reconnected: ['I am back.'],
    errorGeneric: ['Something went wrong on my side. Start again when you are ready.'],
    profileDeleted: ['I have forgotten everything. We can start fresh.'],
    /** Spoken when a slot is captured, to close the loop out loud. */
    acknowledge: ['Got it.', 'Thank you.', 'Noted.'],
    skipped: ['We will skip that.'],

    /**
     * Open-session lines. Namu's own speech models are not wired up yet, and
     * this copy says so plainly rather than improvising an answer. Pretending
     * to understand is the fastest way to lose someone's trust in a product
     * whose entire claim is that it understands them.
     */
    modelNotConnected: [
      'My language model is not connected yet. I can still tell you what I remember about you, change any of it, or take a new name.',
    ],
    helpOffer: [
      'You can say: what do you know about me. Or: call me something else. Or: goodbye.',
    ],
    profileReadIntro: ['Here is what I remember.'],
    profileEmpty: ['I do not have anything saved about you yet.'],
    nameUpdated: ['I will call you {name} from now on.'],
    languageSwitched: ['I will speak English now.'],
  },
}

/**
 * The shape every locale must satisfy. Deliberately inferred without `as const`
 * so the values widen to `string` and `string[]`: other locales have to match
 * the keys, not the English words.
 */
export type Copy = typeof en
